import { TSalesManager } from '@internal/services/TSalesManager';
import { localeNamespace, TLocale } from '@internal/utils/machine/i18n/Locale';
import { CallOutlined, EmailOutlined } from '@mui/icons-material';
import { Button } from '@renderer-ui-library/atoms';
import { ButtonGroup } from '@renderer-ui-library/mui';
import { ContactWizardContext } from '@renderer-ui-library/organisms';
import classNames from 'classnames';
import { translations } from 'i18n/translations';
import { useTranslation } from 'next-i18next';
import React, { SyntheticEvent, useContext } from 'react';
import { MachineContext } from 'utils/machine/MachineContext';
import { trackConversionIntention } from 'utils/tracking/trackConversionIntention';
import { tracker } from 'utils/tracking/tracker';
import { ProfilePicture } from '../Media/ProfilePicture';
import { DynamicContactInfoMobileOverlay } from './dynamicContactInfoMobileOverlay';
import { DynamicContactPhoneSlide } from './dynamicContactPhoneSlide';
import styles from './machineDetailsContact.module.scss';

interface Props {
    isMobile: boolean;
    machineGroupId: string;
    contact: TSalesManager;
    locale: TLocale;
}

export const MachineDetailsContactButtons: React.FC<Props> = React.memo(
    ({ isMobile, machineGroupId, contact, locale }) => {
        const { openRequestInfo } = useContext(ContactWizardContext);
        const { t } = useTranslation(localeNamespace.common);
        const [isDesktopPhoneNumberVisible, setDesktopPhoneNumberVisibility] =
            React.useState(false);
        const { machine } = useContext(MachineContext);

        const handlePhoneClick = React.useCallback(
            (e: SyntheticEvent) => {
                e.preventDefault();
                e.stopPropagation();

                if (!isDesktopPhoneNumberVisible) {
                    if (machine) {
                        trackConversionIntention(machine);
                    }
                    tracker.trackEvent({
                        name: 'call-click',
                        target_name: 'pdp.call_button',
                        is_interaction: true,
                        machine_group_id: machineGroupId,
                    });
                }
                setDesktopPhoneNumberVisibility(
                    showPhoneNumber => !showPhoneNumber,
                );
            },
            [isDesktopPhoneNumberVisible, machineGroupId, machine],
        );

        const handleMessageClick = React.useCallback(
            (e: SyntheticEvent) => {
                e.preventDefault();
                e.stopPropagation();
                openRequestInfo();
            },
            [openRequestInfo],
        );

        return (
            <>
                <div
                    className={classNames({
                        [styles.mobileWrapper]: isMobile,
                        [styles.desktopWrapper]: !isMobile,
                    })}
                >
                    {isMobile && (
                        <Button
                            color="secondary"
                            fullHeight
                            fullWidth
                            noMinWidth
                            onClick={handlePhoneClick}
                        >
                            <div className={styles.imageMobileWrapper}>
                                <ProfilePicture
                                    salesManager={contact}
                                    variant="avatar"
                                    size={150}
                                />
                            </div>
                            <div className={styles.iconContainer}>
                                <CallOutlined className={styles.icon} />
                                {t(translations.machineDetailsContactCallText)}
                            </div>
                        </Button>
                    )}

                    {!isMobile && (
                        <Button
                            fullHeight
                            fullWidth
                            noMinWidth
                            color="secondary"
                            onClick={handlePhoneClick}
                        >
                            <CallOutlined className={styles.icon} />
                            {t(translations.machineDetailsContactCallText)}
                        </Button>
                    )}

                    <ButtonGroup className={styles.buttonGroup}>
                        <Button
                            variant="outlined"
                            disableRipple={false}
                            fullHeight
                            fullWidth
                            noMinWidth
                            onClick={handleMessageClick}
                        >
                            <EmailOutlined className={styles.icon} />
                            {t(translations.machineDetailsContactMessageText)}
                        </Button>
                    </ButtonGroup>

                    {!isMobile && (
                        <DynamicContactPhoneSlide
                            open={isDesktopPhoneNumberVisible}
                            locale={locale}
                            onClose={handlePhoneClick}
                        />
                    )}
                </div>

                {isMobile && (
                    <DynamicContactInfoMobileOverlay
                        open={isDesktopPhoneNumberVisible}
                        contact={contact}
                        onCloseClick={handlePhoneClick}
                        locale={locale}
                    />
                )}
            </>
        );
    },
);

MachineDetailsContactButtons.displayName = 'MachineDetailsContactButtons';
